import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Text, Group, ActionIcon, Anchor, Box, Collapse, Progress } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import TestsApi from "../../api/TestsApi";
import { STATUS, StatusDot } from '../../utils/statusTokens';

// Small sub-component so the useState hook only runs when the mismatch fires
// (avoids hooks-order issues with the early returns in the parent).
function MismatchExplainer({ leaderName, chartTopName }: { leaderName: string, chartTopName: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Box mt={6}>
      <Text
        size="sm"
        sx={(theme) => ({
          color: theme.colorScheme === 'dark' ? theme.colors.gray[4] : theme.colors.gray[7],
        })}
      >
        {'\u{26A0}\u{FE0F}'} Why is {leaderName} the leader? {leaderName} shows fewer conversions on the chart than {chartTopName}, because some visitors to {chartTopName} converted more than once. The engine ranks by the share of unique visitors who converted — the statistically valid basis for choosing a winner — and on that measure {leaderName} is ahead. The chart's conversion count includes repeat conversions, which is correct for revenue but not for rate comparison. This affects Predictive Winner Detection only — your conversion counts and revenue reporting are unchanged.
      </Text>
      <Anchor
        size="sm"
        component="button"
        type="button"
        onClick={() => setExpanded(!expanded)}
        mt={4}
        sx={{ display: 'inline-block', cursor: 'pointer' }}
      >
        Prefer the chart to match the engine? Show me how {expanded ? '▴' : '▾'}
      </Anchor>
      <Collapse in={expanded}>
        <Text
          size="sm"
          mt={6}
          sx={(theme) => ({
            color: theme.colorScheme === 'dark' ? theme.colors.gray[4] : theme.colors.gray[7],
          })}
        >
          Want the chart to match the engine? You can set this conversion to record unique conversions only in your funnel and webhook settings. This makes the chart's count match what the engine already uses — it won't change the leader. Note: it also stops counting repeat conversions toward your revenue totals, so only use it if you don't need repeat purchases tracked on this goal.
        </Text>
      </Collapse>
    </Box>
  );
}

interface PredictiveBannerProps {
  predictiveState: any;
  pageId: string;
  selectedMemberWebsite: string;
  onDismiss: () => void;
  onSetWinner: (varId: number, pageUrl: string) => void;
  goalType: string;
  goalId: string;
  goalLabel?: string;
  testVariationsData?: any[];
  isDefaultDateRange?: boolean;
}

export function PredictiveBanner({ predictiveState, pageId, selectedMemberWebsite, onDismiss, onSetWinner, goalType, goalId, goalLabel = '', testVariationsData, isDefaultDateRange = true }: PredictiveBannerProps) {
  const navigate = useNavigate();
  if (!predictiveState) return null;
  // Variation names as the results table shows them; Control is always "Control" (client feedback Oct 21)
  const vName = (v: any) => {
    const id = Number(v);
    if (id === 1) return 'Control';
    const names = predictiveState.variation_names || {};
    return names[id] || names[String(id)] || `Variation ${id}`;
  };

  // Only one page left live (only Control, or one variation): nothing left to compare.
  // Test-level, so it replaces the banner on every goal view.
  if (predictiveState.one_live && predictiveState.one_live_variation_id) {
    const loneId = Number(predictiveState.one_live_variation_id);
    const loneName = vName(loneId);
    return (
      <Box
        sx={(theme) => ({
          borderLeft: theme.colorScheme === 'dark' ? '4px solid #adb5bd' : `4px solid ${theme.colors.gray[5]}`,
          backgroundColor: theme.colorScheme === 'dark' ? '#2b2b3c' : theme.colors.gray[0],
          padding: '10px 16px',
          marginTop: 12,
          marginBottom: 10,
          borderRadius: 4,
        })}
      >
        <Text size="sm" mb={8} sx={(theme) => ({ color: theme.colorScheme === 'dark' ? '#d0d3db' : undefined })}>
          Only {loneName} is still running, so there's nothing left to compare. Set {loneName} as the winner, or start a new test.
        </Text>
        <Group spacing="sm">
          <Anchor size="sm" fw={600} onClick={() => onSetWinner(loneId, '')}>
            Set Winner
          </Anchor>
          <Text size="sm" color="dimmed">·</Text>
          <Anchor size="sm" fw={600} onClick={() => navigate('/create-test')}>
            New test
          </Anchor>
        </Group>
      </Box>
    );
  }

  if (goalId === 'SDNGWHVZbGZqOTBlaUpYL0tlS2ZxQT09') {
    return (
      <Box
        sx={(theme) => ({
          borderLeft: theme.colorScheme === 'dark' ? '4px solid #adb5bd' : `4px solid ${theme.colors.gray[5]}`,
          borderTop: theme.colorScheme === 'dark' ? '1px solid #46416d' : undefined,
          borderRight: theme.colorScheme === 'dark' ? '1px solid #46416d' : undefined,
          borderBottom: theme.colorScheme === 'dark' ? '1px solid #46416d' : undefined,
          backgroundColor: theme.colorScheme === 'dark' ? '#2b2b3c' : theme.colors.gray[0],
          padding: '8px 16px',
          marginTop: 12,
          marginBottom: 10,
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
        })}
      >
        <Text
          size="sm"
          sx={(theme) => ({ color: theme.colorScheme === 'dark' ? '#b8bcc8' : undefined })}
          color="dimmed"
        >
          {'\u{2139}'} Predictive analysis runs per goal. Select a specific conversion target above to see the engine's predictions.
        </Text>
      </Box>
    );
  }

  const {
    state,
    show_banner,
    pre_threshold,
    leading_variation_id,
    leading_variation_name,
    is_control_winner,
    pause_visible_count,
  } = predictiveState;

  // Runoff (changes 29-31): while it runs, its own line replaces the state banner; an open client choice shows in the
  // slim bar above (the results page), and a called winner gets the runoff's final message.
  const runoff = predictiveState.runoff || null;
  const runoffOpen = !!runoff && (runoff.stage === 'runoff' || runoff.stage === 'client_choice');
  const choiceBar = !!runoff && runoff.choice_open && !runoff.kept_running;
  const nameList = (ids: any[]) => {
    const n = (ids || []).map((v: any) => vName(v));
    return n.length <= 1 ? (n[0] || '') : `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
  };

  // Chart vs engine leader mismatch — strict 4-criteria check.
  // The message may ONLY fire when we can prove the "repeat conversions"
  // explanation is actually true for the specific Y we name, so we never
  // display a confidently wrong explanation. Criteria per updated spec:
  //   (1) Y is NOT the engine leader
  //   (2) Y beats leader on the chart — higher Conv # OR higher Conv %
  //   (3) Y's Conv # (events) > Y's unique-converter count — proves repeat
  //       conversions exist for Y (the event count is inflated)
  //   (4) Leader's unique-CR >= Y's unique-CR — proves the leader IS ahead on
  //       the metric the engine actually ranks by (unique conversion rate)
  // If any criterion fails for every candidate → don't render the message.
  //
  // Gated on isDefaultDateRange: engine data is cumulative from launch through
  // yesterday, so this comparison is only apples-to-apples on the default
  // ("All Time") view. On custom date ranges the chart's window differs from
  // the engine's cumulative window and criteria 3 could pass on non-repeat data.
  let mismatchChartTopVarId: number | null = null;
  const engineData = predictiveState?.variation_engine_data || {};
  if (isDefaultDateRange
      && leading_variation_id !== null && leading_variation_id !== undefined
      && Array.isArray(testVariationsData) && testVariationsData.length > 0
      && engineData && engineData[Number(leading_variation_id)]) {
    const leaderId = Number(leading_variation_id);
    const leaderRow = testVariationsData.find((v: any) => Number(v.test_number) === leaderId);
    const leaderConvNum = leaderRow ? Number(leaderRow.pageconvnum) || 0 : 0;
    const leaderConvPct = leaderRow ? Number(leaderRow.pageconv) || 0 : 0;
    const leaderUniqueCr = Number(engineData[leaderId].unique_cr) || 0;
    // Iterate candidates, pick Y with highest chart conv% among those passing all 4 criteria
    let bestChartVarId: number | null = null;
    let bestChartConvPct = -1;
    let bestChartConvNum = -1;
    for (const v of testVariationsData) {
      const vid = Number(v.test_number);
      if (vid === leaderId) continue; // (1)
      const vConvNum = Number(v.pageconvnum) || 0;
      const vConvPct = Number(v.pageconv) || 0;
      const beatsChart = vConvNum > leaderConvNum || vConvPct > leaderConvPct; // (2)
      if (!beatsChart) continue;
      const vEngine = engineData[vid];
      if (!vEngine) continue;
      const vUniqueConv = Number(vEngine.unique_conv) || 0;
      const vUniqueCr   = Number(vEngine.unique_cr) || 0;
      const hasRepeatConversions = vConvNum > vUniqueConv; // (3)
      if (!hasRepeatConversions) continue;
      const leaderAheadOnUniqueCr = leaderUniqueCr >= vUniqueCr; // (4)
      if (!leaderAheadOnUniqueCr) continue;
      // All 4 criteria passed — Y is a valid candidate. Pick the most visible one.
      if (vConvPct > bestChartConvPct
          || (vConvPct === bestChartConvPct && vConvNum > bestChartConvNum)) {
        bestChartVarId = vid;
        bestChartConvPct = vConvPct;
        bestChartConvNum = vConvNum;
      }
    }
    if (bestChartVarId !== null) mismatchChartTopVarId = bestChartVarId;
  }
  const leaderName = vName(leading_variation_id);
  const chartTopName = vName(mismatchChartTopVarId);

  // Phase 5 — Pause Recommendations secondary line.
  const pauseCount = Number(pause_visible_count || 0);
  // The client-choice bar carries its own message — no pause line alongside it
  const showPauseLine = pauseCount > 0 && state !== 'PREDICTED_WINNER' && !choiceBar;
  // The pause line names the variations the engine has flagged (Build 1 change 41)
  const flaggedIds: number[] = (predictiveState.pause_flagged_variation_ids || []).map((v: any) => Number(v));
  const flaggedNames = flaggedIds.map((v) => vName(v));
  const flaggedList = flaggedNames.length <= 1 ? (flaggedNames[0] || '')
    : `${flaggedNames.slice(0, -1).join(', ')} and ${flaggedNames[flaggedNames.length - 1]}`;
  const pauseLine = flaggedNames.length > 0
    ? `${flaggedList} ${flaggedNames.length === 1 ? 'is' : 'are'} recommended for pause — pausing ${flaggedNames.length === 1 ? 'it' : 'them'} will concentrate traffic on the remaining variations.`
    : `${pauseCount} variation${pauseCount === 1 ? '' : 's'} recommended for pause — pausing them will concentrate traffic on the remaining variations.`;

  // Mismatch line — shared JSX used by TRENDING / PROCEED_WITH_CAUTION / PREDICTED_WINNER banners.
  const mismatchLine = mismatchChartTopVarId !== null
    ? <MismatchExplainer leaderName={leaderName} chartTopName={chartTopName} />
    : null;

  const handleDismiss = () => {
    TestsApi.dismisspredictionbanner({
      system_website_id: selectedMemberWebsite,
      page_id: pageId,
      dismissed_state: state,
      goal_type: goalType,
      goal_id: goalId,
    })
    .then(response => response.json())
    .then(() => {
      onDismiss();
    })
    .catch(() => {});
  };

  // Minimum-data wait (Build 1 change 43): "Waiting on: Variation 2 (5 of 10 conversions), Control (8 of 10)
  // and Variation 5 (8 of 10). All other variations are ready." A unit is spelled out the first time it appears.
  const waitingItems: any[] = Array.isArray(predictiveState.min_data_waiting) ? predictiveState.min_data_waiting : [];
  let waitingLine: string | null = null;
  if (waitingItems.length > 0) {
    let convSeen = false; let visSeen = false;
    const parts = waitingItems.map((w: any) => {
      const nm = vName(w.variation_id);
      const shortV = Number(w.visitors) < 100; const shortC = Number(w.conversions) < 10;
      let p = '';
      if (shortV && shortC) { p = `${w.visitors} of 100 visitors, ${w.conversions} of 10 conversions`; visSeen = true; convSeen = true; }
      else if (shortV) { p = visSeen ? `${w.visitors} of 100` : `${w.visitors} of 100 visitors`; visSeen = true; }
      else { p = convSeen ? `${w.conversions} of 10` : `${w.conversions} of 10 conversions`; convSeen = true; }
      return `${nm} (${p})`;
    });
    const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
    waitingLine = `Waiting on: ${list}.` + (Number(predictiveState.min_data_ready) > 0 ? ' All other variations are ready.' : '');
  }

  // Traffic split block (change 27): the agreed client message while predictions are paused
  if (predictiveState.srm_block) {
    return (
      <Box
        sx={(theme) => ({
          borderLeft: `4px solid ${STATUS.poor}`,
          backgroundColor: theme.colorScheme === 'dark' ? '#3a1f24' : '#fff5f5',
          padding: '8px 16px',
          marginTop: 12,
          marginBottom: 10,
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
        })}
      >
        <Text size="sm" sx={(theme) => ({ color: theme.colorScheme === 'dark' ? '#d0d3db' : undefined })}>
          <StatusDot color={STATUS.poor} />{predictiveState.srm_block}
        </Text>
      </Box>
    );
  }

  // Pre-threshold activation message (only when no pause line is competing for the spot)
  if (pre_threshold && state === 'NO_PREDICTION' && !showPauseLine) {
    return (
      <Box
        sx={(theme) => ({
          borderLeft: theme.colorScheme === 'dark' ? '4px solid #adb5bd' : `4px solid ${theme.colors.gray[5]}`,
          borderTop: theme.colorScheme === 'dark' ? '1px solid #46416d' : undefined,
          borderRight: theme.colorScheme === 'dark' ? '1px solid #46416d' : undefined,
          borderBottom: theme.colorScheme === 'dark' ? '1px solid #46416d' : undefined,
          backgroundColor: theme.colorScheme === 'dark' ? '#2b2b3c' : theme.colors.gray[0],
          padding: '8px 16px',
          marginTop: 12,
          marginBottom: 10,
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
        })}
      >
        <Text
          size="sm"
          sx={(theme) => ({
            color: theme.colorScheme === 'dark' ? '#b8bcc8' : undefined,
          })}
          color="dimmed"
        >
          {'⚪'} Predictive Engine activating: predictions start once every version has 100 unique visitors and 10 conversions.
          {waitingLine && <><br />{waitingLine}</>}
        </Text>
      </Box>
    );
  }

  // Runoff in progress: its start message (or the low-traffic message) in place of the state banner. While the client
  // choice is open the slim bar above carries the message, so nothing more shows here.
  if (runoffOpen) {
    if (choiceBar) return null;
    const finals = (runoff.finalists || []).map((f: any) => f.variation_id);
    const paused = runoff.trigger === 'control_paused' ? ' (Control paused)' : '';
    const line = runoff.kept_running
      ? `Runoff among ${nameList(finals)}${paused}: you chose to keep the test running — the winner is called as soon as the data allows.`
      : runoff.low_traffic
        ? `Runoff among ${nameList(finals)}${paused}: not enough conversions to decide within 7 days at current traffic — you can pick a winner based on business preference, or keep it running.`
        : `Runoff among ${nameList(finals)}${paused}: we'll pick your winner within 7 days, or ask you to choose.`;
    return (
      <Box
        sx={(theme) => ({
          borderLeft: theme.colorScheme === 'dark' ? '4px solid #1971c2' : '4px solid #228be6',
          backgroundColor: theme.colorScheme === 'dark' ? '#1c2e44' : '#e7f5ff',
          padding: '8px 16px',
          marginTop: 12,
          marginBottom: 10,
          borderRadius: 4,
        })}
      >
        <Text size="sm" sx={(theme) => ({ color: theme.colorScheme === 'dark' ? '#a5d8ff' : '#1864ab' })}>
          <StatusDot color={STATUS.trending} />{line}
        </Text>
      </Box>
    );
  }

  // A runoff winner's final message (change 29): where the winner stood against Control
  let runoffWinnerLine: string | null = null;
  if (runoff && runoff.stage === 'called' && runoff.winner) {
    const w = Number(runoff.winner);
    const finals: number[] = (runoff.finalists || []).map((f: any) => Number(f.variation_id));
    const beat = runoff.beat_control_at_start || {};
    const allBeat = runoff.trigger !== 'control_paused' || finals.every((v) => beat[v] !== undefined && beat[v] !== null && Number(beat[v]) >= 0.95);
    // Client messages show no probabilities: the winner's lift vs Control when Control was paused (a runoff started
    // before that figure was stored uses its current lift; Control's numbers stay frozen while it's paused)
    const atStart = (runoff.lift_at_start || {})[w];
    const nowLift = (runoff.finalists || []).find((f: any) => Number(f.variation_id) === w)?.lift_vs_control;
    const lift = atStart !== undefined && atStart !== null ? Number(atStart) : (nowLift !== undefined && nowLift !== null ? Number(nowLift) : null);
    const vsControl = lift === null ? ''
      : lift > 0 ? ` When Control was paused, ${vName(w)} was converting ${lift}% better than Control.`
        : lift < 0 ? ` When Control was paused, ${vName(w)} was converting ${-lift}% worse than Control.`
          : ` When Control was paused, ${vName(w)} was converting at about the same rate as Control.`;
    runoffWinnerLine = allBeat
      ? `${vName(w)} is your winner — best of the ${finals.length} variations that beat Control.`
      : `${vName(w)} is your winner — best of the remaining variations.` + vsControl;
  }

  // Variation label used in copy: "Control" for V1, otherwise "Variation #N"
  const variationLabel = vName(leading_variation_id);

  // Late Conversion Protection (completeness rule) — wording as agreed with the client
  const lcpLeader = vName(leading_variation_id);
  // The goal as client messages name it (from the engine): "HS Book a call conversions", or "conversions on your
  // conversion goal" when the goal has no saved name (client feedback Oct 21)
  const goalConv = predictiveState.goal_conversions || (goalLabel ? `${goalLabel} conversions` : 'conversions on your conversion goal');
  // rounded down, as the engine shows it: a hold below 90% never reads "90%"
  const pctIn = predictiveState.completeness_pct != null ? Math.floor(Number((Number(predictiveState.completeness_pct) * 100).toFixed(4))) : null;
  const lateShare = predictiveState.completeness_late_share != null ? Math.round(Number(predictiveState.completeness_late_share) * 100) : null;
  const slowShare = predictiveState.completeness_slow_share != null ? Math.round(Number(predictiveState.completeness_slow_share) * 100) : null;
  const slowGoalLine = predictiveState.very_slow_goal && slowShare !== null
    ? (
      <Text size="sm" mt={6} color="dimmed">
        This goal converts slowly — about {slowShare}% of conversions arrive more than 3 weeks after the first visit, so predictions take longer. Tests decided on a faster goal resolve sooner.
      </Text>
    )
    : null;

  // Winner held for late conversions: yellow bar with the progress of conversions in
  if (show_banner && state === 'PROCEED_WITH_CAUTION' && predictiveState.completeness_hold && pctIn !== null) {
    return (
      <Box
        sx={(theme) => ({
          borderLeft: theme.colorScheme === 'dark' ? '4px solid #243f41' : '4px solid #fab005',
          backgroundColor: theme.colorScheme === 'dark' ? '#3a3520' : '#fff9db',
          padding: '8px 16px',
          marginTop: 12,
          marginBottom: 10,
          borderRadius: 4,
        })}
      >
        <Group position="apart" align="flex-start" noWrap>
          <Box style={{ flex: 1 }}>
            <Text size="sm" fw={600} color="yellow.8">
              <StatusDot color={STATUS.warning} />Late Conversion Protection
            </Text>
            <Text size="sm" mt={4}>
              {lcpLeader === 'Control'
                ? 'Control is leading and is likely outperforming every variation. '
                : `${lcpLeader} is leading and is likely outperforming Control. `}
              About {lateShare !== null ? lateShare : 0}% of {goalConv} arrive days or weeks after the first visit, so we're waiting for them before calling a winner. Conversions in: {pctIn}% of expected · we'll call it once enough late conversions arrive.
            </Text>
            <Progress value={pctIn} color="yellow" size="sm" mt={8} style={{ maxWidth: 360 }} aria-label={`Conversions in: ${pctIn}% of expected`} />
            {slowGoalLine}
            {showPauseLine && (
              <Text
                size="sm"
                mt={6}
                sx={(theme) => ({
                  color: theme.colorScheme === 'dark' ? theme.colors.orange[4] : theme.colors.orange[8],
                })}
              >
                <StatusDot color={STATUS.pause} />{pauseLine}
              </Text>
            )}
          </Box>
          <ActionIcon size="sm" variant="subtle" onClick={handleDismiss}>
            <IconX size={14} />
          </ActionIcon>
        </Group>
      </Box>
    );
  }

  // PREDICTED_WINNER: green banner (pause line suppressed by rule above)
  if (show_banner && state === 'PREDICTED_WINNER') {
    return (
      <Box
        sx={(theme) => ({
          borderLeft: theme.colorScheme === 'dark' ? '4px solid #243f41' : '4px solid #40c057',
          backgroundColor: theme.colorScheme === 'dark' ? '#243f41' : '#ebfbee',
          padding: '8px 16px',
          marginTop: 12,
          marginBottom: 10,
          borderRadius: 4,
        })}
      >
        <Group position="apart" align="flex-start" noWrap>
          <Box style={{ flex: 1 }}>
            <Group spacing="xs">
              <Text
                size="sm"
                fw={600}
                sx={(theme) => ({
                  color: theme.colorScheme === 'dark' ? '#ffffff' : 'green'
                })}
              >
                <StatusDot color={STATUS.good} />Predicted Winner:
              </Text>
              {runoffWinnerLine ? (
                <Text size="sm">{runoffWinnerLine}</Text>
              ) : predictiveState.completeness_early_call && pctIn !== null ? (
                <Text size="sm">
                  {lcpLeader} is your predicted winner. Based on how long {goalConv} typically take to arrive, about {100 - pctIn}% of expected conversions are still to come — but even if every one of them went against {lcpLeader}, it would still win.
                </Text>
              ) : (
                <Text size="sm">
                  {lcpLeader === 'Control'
                    ? "Control is your winner. It's outperforming every variation, so we recommend keeping it."
                    : `${lcpLeader} is your winner. It's outperforming Control and every other variation, and the lead has held steady.`}
                </Text>
              )}
              <Anchor size="sm" fw={600} onClick={() => onSetWinner(leading_variation_id, '')}>
                Set as Winner
              </Anchor>
            </Group>
            {mismatchLine}
          </Box>
          <ActionIcon size="sm" variant="subtle" onClick={handleDismiss}>
            <IconX size={14} />
          </ActionIcon>
        </Group>
      </Box>
    );
  }

  // PROCEED_WITH_CAUTION: yellow banner with optional pause secondary line
  if (show_banner && state === 'PROCEED_WITH_CAUTION') {
    return (
      <Box
        sx={(theme) => ({
          borderLeft: theme.colorScheme === 'dark' ? '4px solid #243f41' : '4px solid #fab005',
          backgroundColor: theme.colorScheme === 'dark' ? '#3a3520' : '#fff9db',
          padding: '8px 16px',
          marginTop: 12,
          marginBottom: 10,
          borderRadius: 4,
        })}
      >
        <Group position="apart" align="flex-start" noWrap>
          <Box style={{ flex: 1 }}>
            <Group spacing="xs">
              <Text size="sm" fw={600} color="yellow.8">
                <StatusDot color={STATUS.warning} />Leader Emerging:
              </Text>
              <Text size="sm">
                {lcpLeader === 'Control'
                  ? "Control is leading and is likely outperforming every variation. The final winner isn't confirmed yet. We'll notify you as soon as it is."
                  : `${lcpLeader} is leading and is likely outperforming Control. The final winner isn't confirmed yet. We'll notify you as soon as it is.`}
              </Text>
            </Group>
            {slowGoalLine}
            {showPauseLine && (
              <Text
                size="sm"
                mt={6}
                sx={(theme) => ({
                  color: theme.colorScheme === 'dark' ? theme.colors.orange[4] : theme.colors.orange[8],
                })}
              >
                <StatusDot color={STATUS.pause} />{pauseLine}
              </Text>
            )}
            {mismatchLine}
          </Box>
          <ActionIcon size="sm" variant="subtle" onClick={handleDismiss}>
            <IconX size={14} />
          </ActionIcon>
        </Group>
      </Box>
    );
  }

  // TRENDING: blue banner with optional pause secondary line
  if (show_banner && state === 'TRENDING') {
    return (
      <Box
        sx={(theme) => ({
          borderLeft: theme.colorScheme === 'dark' ? '4px solid #1971c2' : '4px solid #228be6',
          backgroundColor: theme.colorScheme === 'dark' ? '#1c2e44' : '#e7f5ff',
          padding: '8px 16px',
          marginTop: 12,
          marginBottom: 10,
          borderRadius: 4,
        })}
      >
        <Group position="apart" align="flex-start" noWrap>
          <Box style={{ flex: 1 }}>
            <Group spacing="xs">
              <Text
                size="sm"
                fw={600}
                sx={(theme) => ({
                  color: theme.colorScheme === 'dark' ? '#a5d8ff' : '#1864ab',
                })}
              >
                <StatusDot color={STATUS.trending} />Trending Leader Identified — {variationLabel}.
              </Text>
              <Text size="sm">
                The engine is forming a directional signal. Statistical confidence is still building. We recommend continuing the test until confidence threshold is met.
              </Text>
            </Group>
            {slowGoalLine}
            {showPauseLine && (
              <Text
                size="sm"
                mt={6}
                sx={(theme) => ({
                  color: theme.colorScheme === 'dark' ? theme.colors.orange[4] : theme.colors.orange[8],
                })}
              >
                <StatusDot color={STATUS.pause} />{pauseLine}
              </Text>
            )}
            {mismatchLine}
          </Box>
          <ActionIcon size="sm" variant="subtle" onClick={handleDismiss}>
            <IconX size={14} />
          </ActionIcon>
        </Group>
      </Box>
    );
  }

  // Fallback: NO_PREDICTION (or any other state) + pause line — secondary line
  // becomes the only content (matches spec Section 4.4 — NO_PREDICTION + losers
  // flagged replaces activation; also covers TRENDING when its banner is dismissed).
  if (showPauseLine) {
    return (
      <Box
        sx={(theme) => ({
          borderLeft: '4px solid #f76707',
          backgroundColor: theme.colorScheme === 'dark' ? '#3a2a1a' : '#fff4e6',
          padding: '8px 16px',
          marginTop: 12,
          marginBottom: 10,
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
        })}
      >
        <Text size="sm"><StatusDot color={STATUS.pause} />{pauseLine}</Text>
      </Box>
    );
  }

  return null;
}
